import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import InitialScreen from "./memorial-creation/InitialScreen";
import ProfileScreen from "./memorial-creation/ProfileScreen";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthTokens } from "@/hooks/useAuthTokens";
import { captureBeforeNavigate } from "@/lib/posthog";

export type MemorialInfo = {
  firstName: string;
  lastName: string;
  hasPassed: boolean;
  memorialPurpose?: 'self' | 'loved_one';
  profilePicture?: File;
  birthDate?: string;
  birthPlace?: string;
  deathDate?: string;
  deathPlace?: string;
  isDoubleMemorial?: boolean;
  person2FirstName?: string;
  person2LastName?: string;
  person2HasDifferentLastName?: boolean;
  person2BirthDate?: string;
  person2DeathDate?: string;
  person2BirthPlace?: string;
  person2DeathPlace?: string;
};

interface MemorialCreationFlowProps {
  onClose?: () => void;
}

const MemorialCreationFlow = ({
  onClose
}: MemorialCreationFlowProps) => {
  const [searchParams] = useSearchParams();
  const [currentScreen, setCurrentScreen] = useState(0);
  const memorialPurpose = searchParams.get('memorialPurpose') as 'self' | 'loved_one' | null;
  
  const [memorialInfo, setMemorialInfo] = useState<MemorialInfo>({
    firstName: "",
    lastName: "",
    hasPassed: false,
    memorialPurpose: memorialPurpose || undefined
  });
  const [canCreateMemorial, setCanCreateMemorial] = useState<boolean>(true);
  const [isChecking, setIsChecking] = useState<boolean>(true);
  const [totalQuantity, setTotalQuantity] = useState<number>(0);
  const [usedCount, setUsedCount] = useState<number>(0);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getAvailableMemorialCount } = useAuthTokens();

  useEffect(() => {
    const checkAvailability = async () => {
      if (!user) return;

      try {
        setIsChecking(true);

        const availableCount = await getAvailableMemorialCount(user.id);
        setCanCreateMemorial(availableCount > 0);

        const { count: tokenCount, error: tokensError } = await supabase
          .from('memorial_tokens')
          .select('id', { count: 'exact', head: true })
          .eq('claimed_by', user.id)
          .eq('claimed', true);

        if (tokensError) throw tokensError;

        const total = tokenCount || 0;
        setTotalQuantity(total);

        const { count, error: memorialsError } = await supabase
          .from('memorials')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('plan_type', 'lifetime');

        if (memorialsError) throw memorialsError;

        setUsedCount(count || 0);
      } catch (error) {
        console.error("Error checking memorial availability:", error);
      } finally {
        setIsChecking(false);
      }
    };

    checkAvailability();
  }, [user, getAvailableMemorialCount]);

  const handleNext = () => {
    setCurrentScreen(prev => prev + 1);
  };

  const handleBack = () => {
    setCurrentScreen(prev => Math.max(0, prev - 1));
  };

  const updateMemorialInfo = (updates: Partial<MemorialInfo>) => {
    setMemorialInfo(prev => ({
      ...prev,
      ...updates
    }));
  };

  const ensureAdminExists = async (userId: string, email: string) => {
    const {
      data: existingAdmin,
      error: checkError
    } = await supabase.from('admins').select('*').eq('id', userId).single();
    if (checkError && checkError.code !== 'PGRST116') {
      console.error('Error checking admin existence:', checkError);
      throw checkError;
    }
    if (existingAdmin) {
      return existingAdmin;
    }
    const {
      data: newAdmin,
      error: insertError
    } = await supabase.from('admins').insert({
      id: userId,
      email: email,
      active: true
    }).select().single();
    if (insertError) {
      console.error('Error creating admin record:', insertError);
      throw insertError;
    }
    return newAdmin;
  };

  const handleFinish = async () => {
    // Prevent multiple submissions
    if (isCreating) {
      return;
    }

    if (!canCreateMemorial) {
      toast.error("You've reached your memorial limit");
      if (onClose) {
        onClose();
      } else {
        navigate("/memorials");
      }
      return;
    }

    try {
      setIsCreating(true);
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in to create a memorial");
        return;
      }
      
      const availableCount = await getAvailableMemorialCount(user.id);
      if (availableCount <= 0) {
        toast.error("You've reached your memorial limit");
        if (onClose) {
          onClose();
        } else {
          navigate("/memorials");
        }
        return;
      }
      
      // Validate that death date is on or after birth date
      if (memorialInfo.birthDate && memorialInfo.deathDate) {
        const birthDate = new Date(memorialInfo.birthDate);
        const deathDate = new Date(memorialInfo.deathDate);

        if (deathDate < birthDate) {
          toast.error("Death date must be on or after birth date");
          return;
        }
      }

      // Validate person 2 dates if double memorial
      if (memorialInfo.isDoubleMemorial && memorialInfo.person2BirthDate && memorialInfo.person2DeathDate) {
        const p2BirthDate = new Date(memorialInfo.person2BirthDate);
        const p2DeathDate = new Date(memorialInfo.person2DeathDate);
        if (p2DeathDate < p2BirthDate) {
          toast.error("Second person's death date must be on or after their birth date");
          return;
        }
      }
      
      let profilePictureUrl = null;
      if (memorialInfo.profilePicture) {
        const fileExt = memorialInfo.profilePicture.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const {
          error: uploadError,
          data: uploadData
        } = await supabase.storage.from('memorials').upload(`profile-pictures/${fileName}`, memorialInfo.profilePicture);
        if (uploadError) {
          throw uploadError;
        }
        if (uploadData) {
          const {
            data: {
              publicUrl
            }
          } = supabase.storage.from('memorials').getPublicUrl(`profile-pictures/${fileName}`);
          profilePictureUrl = publicUrl;
        }
      }
      
      const birthDateStr = memorialInfo.birthDate || null;
      const deathDateStr = memorialInfo.deathDate || null;
      
      const {
        error: insertError,
        data: insertedMemorial
      } = await supabase.from('memorials').insert({
        user_id: user.id,
        first_name: memorialInfo.firstName,
        last_name: memorialInfo.lastName,
        has_passed: memorialInfo.hasPassed,
        memorial_purpose: memorialInfo.memorialPurpose || null,
        birth_date: birthDateStr,
        death_date: deathDateStr,
        profile_picture_url: profilePictureUrl,
        place_of_birth: memorialInfo.birthPlace,
        place_of_death: memorialInfo.deathPlace,
        plan_type: 'lifetime',
        expires_at: null,
        is_active: true,
        is_double_memorial: memorialInfo.isDoubleMemorial || false,
        person2_first_name: memorialInfo.isDoubleMemorial ? memorialInfo.person2FirstName || null : null,
        person2_last_name: memorialInfo.isDoubleMemorial && memorialInfo.person2HasDifferentLastName
          ? memorialInfo.person2LastName?.trim() || null
          : null,
        person2_birth_date: memorialInfo.isDoubleMemorial ? (memorialInfo.person2BirthDate ? new Date(memorialInfo.person2BirthDate).toISOString().split('T')[0] : null) : null,
        person2_death_date: memorialInfo.isDoubleMemorial ? (memorialInfo.person2DeathDate ? new Date(memorialInfo.person2DeathDate).toISOString().split('T')[0] : null) : null,
        person2_place_of_birth: memorialInfo.isDoubleMemorial ? memorialInfo.person2BirthPlace || null : null,
        person2_place_of_death: memorialInfo.isDoubleMemorial ? memorialInfo.person2DeathPlace || null : null,
      }).select().single();
      
      if (insertError) throw insertError;

      if (insertedMemorial) {
        const directUrl = `${window.location.origin}/memorial/${insertedMemorial.id}`;
        const {
          error: urlError
        } = await supabase.from('memorials').update({
          memorial_url: directUrl
        }).eq('id', insertedMemorial.id);
        if (urlError) {
          console.error("Error storing memorial URL:", urlError);
        }

      }

      toast.success("Memorial created successfully!");

      // sendBeacon-backed: the navigate() below was racing the regular flush
      // and losing the event (same H-035 / H-037 pattern).
      captureBeforeNavigate('memorial_created', {
        memorial_id: insertedMemorial.id,
        is_b2b: false,
        purpose: memorialInfo.memorialPurpose ?? null,
        monument_company_id: null,
      });

      // Navigate to the edit-memorial page for the new memorial
      navigate(`/edit-memorial?memorialId=${insertedMemorial.id}`);
    } catch (error: unknown) {
      console.error("Error creating memorial:", error);
      toast.error(error instanceof Error ? error.message : "Error creating memorial");
    } finally {
      setIsCreating(false);
    }
  };

  const handlePurchase = () => {
    window.location.href = "mailto:sales@pontis.life?subject=Purchase%20Memorial%20Medallion";
  };

  const screens = [
    <InitialScreen key="initial" memorialInfo={memorialInfo} onUpdate={updateMemorialInfo} onNext={handleNext} />,
    <ProfileScreen key="profile" memorialInfo={memorialInfo} onUpdate={updateMemorialInfo} onNext={handleFinish} isCreating={isCreating} />
  ];

  return (
    <div className="bg-brand-background p-4 md:p-6 min-h-fit">
      {isChecking ? (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      ) : !canCreateMemorial ? (
        <div className="text-center space-y-4 p-8">
          <h3 className="text-xl font-semibold text-red-600">
            Memorial Limit Reached
          </h3>
          <div className="mt-2 mb-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Total Memorial Credits:</span>
              <span className="font-medium">{totalQuantity}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Credits Used:</span>
              <span className="font-medium">{usedCount}</span>
            </div>
            <div className="h-2 w-full bg-gray-200 rounded-full mt-3">
              <div
                className="h-2 bg-brand-primary rounded-full"
                style={{ width: `${totalQuantity > 0 ? (usedCount / totalQuantity) * 100 : 100}%` }}
              ></div>
            </div>
          </div>
          <p>
            You've used all your available memorials. Please purchase more to continue creating memorials.
          </p>
          <div className="flex justify-center space-x-4 mt-6">
            <Button onClick={handlePurchase} variant="default" className="bg-brand-primary hover:bg-brand-primary/90">
              Purchase Another Memorial
            </Button>
            <Button onClick={onClose} variant="outline">Close</Button>
          </div>
        </div>
      ) : (
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="font-serif text-brand-primary text-3xl">
            Create New Memorial
          </h2>
          <p className="text-muted-foreground">
            Fill in the details to create a new memorial page
          </p>
          
          {currentScreen > 0 && <Button variant="ghost" onClick={handleBack}>
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>}
          <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
            {screens[currentScreen]}
          </div>
        </div>
      )}
    </div>
  );
};

export default MemorialCreationFlow;
